import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
  catchError,
  forkJoin,
  map,
  merge,
  Observable,
  of,
  scan,
  startWith,
  switchMap,
  throwError,
} from 'rxjs';
import { API_BASE_URL } from '../../../shared/config/api.config';
import { Product } from '../../home/models/product.model';
import { pickDefaultStyle, toCategory, toProductCard, toProductDetail } from '../mappers/catalog.mapper';
import {
  BranchApi,
  CategoryApi,
  ProductApi,
  ProductImageApi,
  ProductItemApi,
  ProductStyleApi,
  SizeApi,
  SubCategoryApi,
} from '../models/catalog-api.model';
import { Category } from '../models/category.model';
import { ProductDetailProgress } from '../models/product-detail.model';

const MAX_PAGE_SIZE = 200;

// Respuestas por estilo acumuladas a medida que llegan; undefined = todavía pendiente.
interface StyleParts {
  images?: ProductImageApi[];
  items?: ProductItemApi[];
  sizes?: SizeApi[];
}

@Injectable({ providedIn: 'root' })
export class CatalogService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = API_BASE_URL;

  listProducts(): Observable<Product[]> {
    const now = new Date();
    return this.fetchProducts().pipe(
      map((products) => products.map((product) => toProductCard(product, now))),
    );
  }

  listCategories(): Observable<Category[]> {
    return this.fetchCategories().pipe(map((categories) => categories.map(toCategory)));
  }

  /** Solo lo consume la página de categorías para resolver a qué categoría pertenece cada producto. */
  listSubCategories(): Observable<SubCategoryApi[]> {
    return this.http.get<SubCategoryApi[]>(`${this.baseUrl}/sub-categories`, {
      params: activeFullPageParams(),
    });
  }

  /** Idem listSubCategories: es el último eslabón antes de productId.productBranchId. */
  listBranches(): Observable<BranchApi[]> {
    return this.http.get<BranchApi[]>(`${this.baseUrl}/branches`, {
      params: activeFullPageParams(),
    });
  }

  /**
   * Emite el detalle por etapas para que la página lo muestre a medida que llega: primero
   * con los datos del producto (título, descripción), después con las imágenes y con las
   * variantes (tallas y precio) del estilo elegido, en el orden en que respondan. La última
   * emisión es el detalle completo. null si el producto no existe o está inactivo.
   *
   * `styleId` es opcional: permite pedir el detalle ya centrado en un color puntual sin
   * agregar ningún endpoint nuevo — es la base para el futuro selector de color. Si no
   * viene, o el estilo pedido no está activo, se usa el estilo por defecto.
   */
  getProductDetail(id: number, styleId?: number): Observable<ProductDetailProgress | null> {
    return this.fetchProduct(id).pipe(
      catchError((error: HttpErrorResponse) =>
        error.status === 404 ? of(null) : throwError(() => error),
      ),
      switchMap((product) => (product?.isActive ? this.streamDetail(product, styleId) : of(null))),
    );
  }

  private streamDetail(product: ProductApi, styleId?: number): Observable<ProductDetailProgress> {
    const onlyProduct: ProductDetailProgress = {
      detail: toProductDetail(product, [], null, [], [], []),
      imagesReady: false,
      variantsReady: false,
    };
    return this.fetchStyles(product.id).pipe(
      switchMap((styles) => this.streamStyleParts(product, styles, styleId)),
      startWith(onlyProduct),
    );
  }

  // Imágenes por un lado y items + tallas por otro: cada grupo actualiza su bloque apenas
  // llega, sin esperar al otro.
  private streamStyleParts(
    product: ProductApi,
    styles: ProductStyleApi[],
    styleId?: number,
  ): Observable<ProductDetailProgress> {
    const chosenStyle = this.chooseStyle(styles, styleId);
    if (!chosenStyle) {
      return of({
        detail: toProductDetail(product, styles, null, [], [], []),
        imagesReady: true,
        variantsReady: true,
      });
    }
    const images$ = this.fetchImages(chosenStyle.id).pipe(map((images) => ({ images })));
    const variants$ = forkJoin({
      items: this.fetchItems(chosenStyle.id),
      sizes: this.fetchSizes(),
    });
    return merge<StyleParts[]>(images$, variants$).pipe(
      scan((parts, part) => ({ ...parts, ...part }), {} as StyleParts),
      map(({ images, items, sizes }) => ({
        detail: toProductDetail(product, styles, chosenStyle, images ?? [], items ?? [], sizes ?? []),
        imagesReady: images !== undefined,
        variantsReady: items !== undefined,
      })),
    );
  }

  private chooseStyle(styles: ProductStyleApi[], styleId?: number): ProductStyleApi | null {
    const requested = styles.find((style) => style.id === styleId && style.isActive);
    return requested ?? pickDefaultStyle(styles);
  }

  // Los más nuevos primero (sort=newest del catálogo): lo recién cargado encabeza el carrusel en
  // vez de quedar al final, detrás de cien tarjetas.
  private fetchProducts(): Observable<ProductApi[]> {
    return this.http.get<ProductApi[]>(`${this.baseUrl}/products`, {
      params: activeFullPageParams().set('sort', 'newest'),
    });
  }

  private fetchCategories(): Observable<CategoryApi[]> {
    return this.http.get<CategoryApi[]>(`${this.baseUrl}/categories`, {
      params: activeFullPageParams(),
    });
  }

  private fetchProduct(id: number): Observable<ProductApi> {
    return this.http.get<ProductApi>(`${this.baseUrl}/products/${id}`);
  }

  private fetchStyles(productId: number): Observable<ProductStyleApi[]> {
    const params = new HttpParams().set('productId', productId).set('onlyActive', true);
    return this.http.get<ProductStyleApi[]>(`${this.baseUrl}/product-styles`, { params });
  }

  private fetchImages(styleId: number): Observable<ProductImageApi[]> {
    return this.http.get<ProductImageApi[]>(`${this.baseUrl}/product-styles/${styleId}/images`);
  }

  private fetchItems(styleId: number): Observable<ProductItemApi[]> {
    const params = activeFullPageParams().set('productStyleId', styleId);
    return this.http.get<ProductItemApi[]>(`${this.baseUrl}/product-items`, { params });
  }

  private fetchSizes(): Observable<SizeApi[]> {
    return this.http.get<SizeApi[]>(`${this.baseUrl}/sizes`, { params: activeFullPageParams() });
  }
}

// Solo registros activos y la página más grande que acepta el backend: la tienda tiene
// pocas decenas de productos, así que una sola página alcanza.
function activeFullPageParams(): HttpParams {
  return new HttpParams().set('onlyActive', true).set('size', MAX_PAGE_SIZE);
}
